import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Users, Loader2, History, ChevronDown, MessageSquare, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const AUDIENCES = [
  { value: "all", label: "Semua Jamaah (dengan nomor WA)" },
  { value: "active_only", label: "Jamaah Aktif Saja" },
  { value: "with_bookings", label: "Jamaah dengan Booking" },
];

const TEMPLATES = [
  {
    label: "Promo Paket Baru",
    text: "Assalamualaikum {{name}} 🙏\n\nAlhamdulillah, Batik Travel membuka paket Umrah terbaru:\n\n📅 [Tanggal Keberangkatan]\n💰 Mulai Rp [Harga]\n✈️ Direct Flight Jakarta\n\nInfo & pendaftaran: wa.me/6282114294101\n\nBarakallahu fiik 🌙",
  },
  {
    label: "Reminder Manasik",
    text: "Assalamualaikum {{name}} 🙏\n\nMengingatkan jadwal manasik umrah kita:\n\n📅 [Hari], [Tanggal]\n📍 Kantor Batik Travel\n⏰ [Jam]\n\nMohon hadir tepat waktu. Terima kasih 🌙",
  },
  {
    label: "Terima Kasih Alumni",
    text: "Assalamualaikum {{name}} 🙏\n\nTerima kasih telah mempercayakan perjalanan umrah Anda kepada Batik Travel. Semoga menjadi umrah yang mabrur.\n\nJangan ragu untuk merekomendasikan Batik Travel kepada keluarga dan teman. Barakallahu fiik 🌙",
  },
];

export default function BroadcastWA() {
  const [counts, setCounts] = useState({});
  const [audience, setAudience] = useState("with_bookings");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [testMode, setTestMode] = useState(true);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get("/admin/broadcast/audience-counts").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/admin/broadcast/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) { toast.error("Pesan tidak boleh kosong"); return; }
    if (!testMode && !window.confirm(`Kirim pesan ke ${counts[audience] || "?"} nomor? Ini akan langsung mengirim WhatsApp.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/broadcast/whatsapp", { message, audience, test_mode: testMode });
      setResult(data);
      toast.success(`Terkirim ${data.sent}/${data.total}`);
      api.get("/admin/broadcast/history").then((r) => setHistory(r.data));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="broadcast-wa-page" className="max-w-4xl">
      <div className="mb-8">
        <h2 className="font-serif text-3xl text-primary">Bulk WhatsApp Blast</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Kirim broadcast pesan promo, reminder manasik, atau info penting via Fonnte ke seluruh jamaah.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6">
        {/* Audience */}
        <div className="bg-card border border-border rounded-lg p-6">
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-2">
            <Users className="h-3.5 w-3.5" /> Target Audiens
          </label>
          <div className="mt-3 grid sm:grid-cols-3 gap-3">
            {AUDIENCES.map((a) => (
              <label
                key={a.value}
                data-testid={`audience-${a.value}`}
                className={`cursor-pointer border rounded-lg p-4 transition-all ${
                  audience === a.value ? "border-accent bg-accent/10" : "border-border hover:border-accent/50"
                }`}
              >
                <input type="radio" name="audience" value={a.value} checked={audience === a.value} onChange={(e) => setAudience(e.target.value)} className="hidden" />
                <div className="text-sm font-medium text-primary">{a.label}</div>
                <div className="mt-1 font-serif text-2xl text-accent">{counts[a.value] ?? "-"}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Nomor tersedia</div>
              </label>
            ))}
          </div>
        </div>

        {/* Templates */}
        <div className="bg-card border border-border rounded-lg p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-2 mb-3">
            <MessageSquare className="h-3.5 w-3.5" /> Template Cepat
          </p>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setMessage(t.text)}
                data-testid={`template-${i}`}
                className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-accent hover:text-accent transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message */}
        <div className="bg-card border border-border rounded-lg p-6">
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Pesan WhatsApp</label>
          <p className="text-xs text-muted-foreground mt-1 mb-2">
            Gunakan <code className="text-accent">{`{{name}}`}</code> untuk personalisasi nama jamaah.
          </p>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={10}
            placeholder="Ketik pesan Anda…"
            data-testid="broadcast-message"
            className="font-mono text-sm"
          />
          <div className="mt-2 text-xs text-muted-foreground flex justify-between">
            <span>{message.length} / 4000 karakter</span>
            {message.includes("{{name}}") && <span className="text-accent">✓ Personalisasi aktif</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid="test-mode-toggle" />
            <span className="text-primary">Mode Uji Coba (kirim ke 1 nomor pertama saja)</span>
          </label>
          <Button
            type="submit"
            disabled={busy || !message.trim()}
            data-testid="broadcast-send-btn"
            className="inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {testMode ? "Kirim Test" : "Kirim ke Semua"}
          </Button>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-card border border-border rounded-lg p-6" data-testid="broadcast-result">
            <p className="font-medium text-primary">Hasil Broadcast</p>
            <div className="mt-3 grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="font-serif text-2xl text-primary">{result.total}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Terkirim</p>
                <p className="font-serif text-2xl text-green-600">{result.sent}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gagal</p>
                <p className="font-serif text-2xl text-red-600">{result.failed}</p>
              </div>
            </div>
            {result.sample_errors?.length > 0 && (
              <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-red-700 flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5" /> Sampel Error ({result.sample_errors.length})
                </summary>
                <ul className="mt-2 space-y-1 text-red-600">
                  {result.sample_errors.map((e, i) => (
                    <li key={i}>{e.phone} — {e.error}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {/* History */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full flex items-center justify-between p-6 hover:bg-muted/30"
            data-testid="broadcast-history-toggle"
          >
            <span className="font-medium text-primary flex items-center gap-2">
              <History className="h-4 w-4 text-accent" /> Riwayat Broadcast ({history.length})
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showHistory ? "rotate-180" : ""}`} />
          </button>
          {showHistory && (
            <ul className="border-t border-border divide-y divide-border">
              {history.length === 0 && <li className="p-6 text-center text-muted-foreground text-sm">Belum ada broadcast.</li>}
              {history.map((h) => (
                <li key={h.id} className="p-4 hover:bg-muted/20" data-testid={`history-${h.id}`}>
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-primary">{h.message_preview}…</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(h.at).toLocaleString("id-ID")} • audiens: {h.audience}
                      </p>
                    </div>
                    <div className="text-right text-xs">
                      <p className="text-green-600 font-medium">✓ {h.sent}</p>
                      {h.failed > 0 && <p className="text-red-500">✗ {h.failed}</p>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </form>
    </div>
  );
}
